/**
 * NUMBER, PERCENTAGE and DATE custom fields (2026-10-31).
 *
 * A SELECTOR's value is the picked option ids. EVERY other type keeps its
 * value as text in `customInputScopes[].textValue` — NUMBERS, PERCENTAGE and
 * DATE exactly like TEXT_FIELD; their text just has a format:
 *
 *   NUMBERS     "120000", "-3.5"
 *   PERCENTAGE  "45.5"            (0 to 100, no "%" stored)
 *   DATE        "2026-10-31"      (never later than the field's `dateMax`)
 *
 * The server checks and normalises the same formats on every write
 * (`cf-value-types.ts` there); these helpers keep the inputs and the display
 * in step with it. Use `isTextValuedCfType` instead of a hand-written
 * `TEXT_FIELD || REMARK` test — that test renders a Number field as an empty
 * selector.
 */
import { getTodayYmdIST } from "@/utils/istCalendar";

/** Every type whose value lives in `textValue`. */
export const TEXT_VALUED_CF_TYPES: ReadonlySet<string> = new Set([
  "TEXT_FIELD",
  "REMARK",
  "NUMBERS",
  "PERCENTAGE",
  "DATE",
]);

/** Text-valued types whose text has a format. */
export const FORMATTED_CF_TYPES: ReadonlySet<string> = new Set([
  "NUMBERS",
  "PERCENTAGE",
  "DATE",
]);

export function isTextValuedCfType(type: unknown): boolean {
  return TEXT_VALUED_CF_TYPES.has(String(type ?? ""));
}

export function isFormattedCfType(type: unknown): boolean {
  return FORMATTED_CF_TYPES.has(String(type ?? ""));
}

/** How each type is named to a person (admin form, field table, pickers). */
export const CF_TYPE_LABELS: Record<string, string> = {
  SELECTOR: "Selector",
  TEXT_FIELD: "Text Field",
  REMARK: "Remark",
  NUMBERS: "Number",
  PERCENTAGE: "Percentage",
  DATE: "Date",
};

export const cfTypeLabel = (type: unknown): string =>
  CF_TYPE_LABELS[String(type ?? "")] ?? String(type ?? "");

/* ------------------------------------------------------------------ */
/* DATE LIMIT — `CustomInput.dateMax`                                  */
/* ------------------------------------------------------------------ */

/** `dateMax` meaning "up to today" (no future dates). */
export const CF_DATE_MAX_TODAY = "today";

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function isValidYmd(value: string): boolean {
  const m = YMD_RE.exec(value ?? "");
  if (!m) return false;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1) return false;
  return d <= new Date(Date.UTC(y, mo, 0)).getUTCDate();
}

/** Stored `dateMax` → latest allowed day "YYYY-MM-DD"; undefined = no limit. */
export function resolveCfDateMax(
  dateMax: string | null | undefined,
  now: Date = new Date(),
): string | undefined {
  if (!dateMax) return undefined;
  if (dateMax === CF_DATE_MAX_TODAY) return getTodayYmdIST(now);
  return isValidYmd(dateMax) ? dateMax : undefined;
}

/** "2026-10-31" → "31 Oct 2026". Anything else is returned as it is. */
export function formatCfDate(value: string): string {
  const m = YMD_RE.exec(String(value ?? "").trim());
  if (!m) return String(value ?? "");
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
}

/** Human description of a field's date limit, for the admin form + hints. */
export function describeCfDateMax(dateMax: string | null | undefined): string {
  if (!dateMax) return "Any date";
  if (dateMax === CF_DATE_MAX_TODAY) return "Up to today (no future dates)";
  return isValidYmd(dateMax) ? `Up to ${formatCfDate(dateMax)}` : "Any date";
}

/* ------------------------------------------------------------------ */
/* DISPLAY                                                             */
/* ------------------------------------------------------------------ */

const NUMBER_RE = /^-?(?:\d+(?:\.\d+)?|\.\d+)$/;

/**
 * A stored text value as a person should read it: Percentage → "45.5%",
 * Date → "31 Oct 2026". Numbers stay exactly as typed (a year of passing must
 * not read "2,019"); text / remark values and anything that doesn't parse
 * (old data) are shown exactly as stored. Same rule as the server's
 * `formatCfTextForDisplay`, which formats the lead cards.
 */
export function formatCfTextValue(type: unknown, value: unknown): string {
  const text = value == null ? "" : String(value);
  const trimmed = text.trim();
  if (!trimmed) return text;
  switch (String(type ?? "")) {
    case "PERCENTAGE":
      return NUMBER_RE.test(trimmed) ? `${trimmed}%` : text;
    case "DATE":
      return formatCfDate(trimmed);
    default:
      return text;
  }
}

/* ------------------------------------------------------------------ */
/* INPUT                                                               */
/* ------------------------------------------------------------------ */

/**
 * Keystroke filter for Number / Percentage inputs: digits, one point and (for
 * numbers) a leading minus. Keeps half-typed values like "-" or "12." so the
 * person can finish typing; the server normalises the final value.
 */
export function sanitizeCfNumberInput(raw: string, allowNegative: boolean): string {
  let s = String(raw ?? "").replace(/[^\d.-]/g, "");
  const negative = allowNegative && s.startsWith("-");
  s = s.replace(/-/g, "");
  const dot = s.indexOf(".");
  if (dot !== -1) s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, "");
  return negative ? `-${s}` : s;
}

/**
 * The problem with a typed value, as the server will word it, or null when
 * it's fine. Blank is fine (it clears the field). Lets a form stop on the
 * field itself instead of a toast after the round trip.
 */
export function cfTextValueProblem(
  field: { type?: string | null; name?: string | null; label?: string | null; dateMax?: string | null },
  value: unknown,
  now: Date = new Date(),
): string | null {
  const text = value == null ? "" : String(value).trim();
  if (!text || !isFormattedCfType(field.type)) return null;
  const name = field.label?.trim() || field.name || "This field";
  const plain = text.replace(/[,\s]/g, "");
  if (field.type === "NUMBERS") {
    return NUMBER_RE.test(plain) ? null : `${name} must be a number.`;
  }
  if (field.type === "PERCENTAGE") {
    const p = plain.replace(/%$/, "");
    if (!NUMBER_RE.test(p)) return `${name} must be a percentage, e.g. 45 or 72.5.`;
    const n = Number(p);
    return n < 0 || n > 100 ? `${name} must be between 0 and 100.` : null;
  }
  // DATE — the inputs only ever produce "YYYY-MM-DD".
  if (!isValidYmd(text)) return `${name} must be a date (DD/MM/YYYY).`;
  const max = resolveCfDateMax(field.dateMax, now);
  if (max && text > max) {
    return field.dateMax === CF_DATE_MAX_TODAY
      ? `${name} can't be a future date.`
      : `${name} can't be after ${formatCfDate(max)}.`;
  }
  return null;
}
